import { detect } from 'detect-browser';

import { type IMediator } from '#/core/mediator';

import { type IConfigProviderService } from '#/services/config-provider';
import { CookieName } from '#/services/cookie/cookie.types';
import { type IUrlParamsService, UrlParamsName } from '#/services/url-params';

import { SystemError } from '#/shared/exceptions/system-error';
import { isInIframe } from '#/shared/utils/is-in-iframe';
import { safeJsonParse } from '#/shared/utils/safe-json-parse';

import { SERVICES_TYPES } from '#/services-types';

import { type ICookieService } from '../cookie';
import { type ILogService } from '../log/log.types';

import { AndroidTradingService } from './android-trading.service';
import { DeviceType, type FeaturesOptions, type IDeviceService, type ModeConfig, type ParentApp } from './device.types';
import { IosTradingService } from './ios-trading.service';
import { WebTradingService } from './web-trading.service';

export class DeviceService implements IDeviceService {
  readonly #serviceName = 'DeviceService';

  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  get #cookieService() {
    return this._mediator.get<ICookieService>(SERVICES_TYPES.CookieService);
  }

  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }

  get #configProviderService() {
    return this._mediator.get<IConfigProviderService>(SERVICES_TYPES.ConfigProviderService);
  }

  #createProxyParentApp(parentApp: ParentApp): ParentApp {
    return new Proxy(parentApp, {
      get: (target: ParentApp, property) => {
        const origin = target[property as keyof ParentApp];

        if (typeof origin === 'function') {
          return (...args: unknown[]) => {
            this.#logService.info(this.#serviceName, 'Call parent callback.', {
              instance: parentApp.serviceName,
              method: property,
              args,
            });

            try {
              // @ts-ignore
              const callbackResult = origin.apply(parentApp, args);

              this.#logService.info(this.#serviceName, 'Result from mobile callback.', {
                instance: parentApp.serviceName,
                method: property,
                callbackResult,
              });

              return callbackResult;
            } catch (error) {
              this.#logService.error(
                this.#serviceName,
                new SystemError('DeviceService calling method error', {
                  instance: parentApp.serviceName,
                  method: property,
                  args,
                  error,
                })
              );
            }
          };
        }

        return origin;
      },
    });
  }

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');

    const androidTradingService = new AndroidTradingService();
    const iosTradingService = new IosTradingService();
    const webTradingService = new WebTradingService();

    const modeConfig = safeJsonParse<ModeConfig | undefined>(this.#cookieService.get(CookieName.ModeConfig));

    if (modeConfig?.device) {
      if (androidTradingService.isDevice(modeConfig.device)) {
        this.deviceType = DeviceType.AndroidNative;
        this.parentApp = this.#createProxyParentApp(androidTradingService);

        this.#logService.info(this.#serviceName, 'Detect Android Webview.');

        return;
      }

      if (iosTradingService.isDevice(modeConfig.device)) {
        this.deviceType = DeviceType.IosNative;
        this.parentApp = this.#createProxyParentApp(iosTradingService);

        this.#logService.info(this.#serviceName, 'Detect iOS Webview.');

        return;
      }
    }

    if (isInIframe() && this.#urlParamsService.getParamByName(UrlParamsName.AppId) === 'WT') {
      this.deviceType = DeviceType.Iframe;
      this.parentApp = this.#createProxyParentApp(webTradingService);

      this.#logService.info(this.#serviceName, 'Detect run in iframe.');

      return;
    }

    const browserInfo = detect();

    if (browserInfo?.os === 'Android OS' || browserInfo?.os === 'iOS') {
      this.deviceType = DeviceType.MobileBrowser;

      this.#logService.info(this.#serviceName, 'Detect mobile browser.');

      return;
    }

    this.#logService.info(this.#serviceName, 'Detect desktop browser.');

    this.deviceType = DeviceType.DesktopBrowser;
  }

  parentApp: ParentApp | undefined;

  deviceType: DeviceType;

  get featureOptions(): FeaturesOptions | undefined {
    return this.#configProviderService.featureOptions;
  }
}
